import { flow, uniq } from "lodash";
import { map } from "lodash/fp";

/**
 * Returns a new array with the items sorted alphanumerically (including non-ASCII)
 */
export function sort(array: string[]): string[] {
    return [...array].sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
    );
}

/**
 * Returns a new array with items sorted alphanumerically (including non-ASCII) and without duplicates
 */
export function uniqSort(array: string[]): string[] {
    return flow(uniq, sort)(array);
}

/**
 * Returns a function that will replace all undefined or null values in an array with 0s.
 */
export const cleanData = map((data: number | undefined | null) => data ?? 0);

/**
 * Deletes null properties as Apple Watch can crash on these. MUTATES OBJECT!
 * @param obj
 * @param recurse
 */
export function deleteNullProperties(obj: any, recurse: boolean) {
    for (var i in obj) {
        if (obj[i] === null) {
            delete obj[i];
        } else if (recurse && typeof obj[i] === "object") {
            deleteNullProperties(obj[i], recurse);
        }
    }
}

/**
 * Trim, collapse runs of whitespace and lowercase, so free-text names that differ only in how
 * they were typed compare equal.
 */
export function normaliseName(name: string): string {
    return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Like {@link uniqSort}, but entries differing only in case or whitespace collapse into one.
 *
 * Names such as venues are typed by hand on every match, so the same ground arrives as
 * "STEYNING", "steyning" and "Steyning ". The spelling kept is a mixed-case one where the user
 * has typed one, since that reads better in a list than either shouted or lowercased text.
 */
export function uniqSortIgnoringCase(array: string[]): string[] {
    const byName = new Map<string, string>();
    array.forEach((value) => {
        const key = normaliseName(value);
        const kept = byName.get(key);
        if (kept === undefined || (!isMixedCase(kept) && isMixedCase(value))) {
            byName.set(key, value);
        }
    });
    return sort(Array.from(byName.values()));
}

function isMixedCase(value: string): boolean {
    return value !== value.toLowerCase() && value !== value.toUpperCase();
}
